import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ConfirmationPage.css';
import api from '../../api/axios';

const ConfirmationPage = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const autoSent = useRef(false);

  const params = new URLSearchParams(location.search);
  const email = params.get('email') || 'your email address';
  const verificationToken = params.get('token');

  const checkStatus = async ({ navigateWhenVerified = true } = {}) => {
    setChecking(true);
    try {
      const response = await api.get('/auth/verification-status');
      if (response.data?.emailVerified === true) {
        setMessage('Your email address has been verified successfully.');
        setResendMessage('');
        if (navigateWhenVerified) navigate('/dashboard', { replace: true });
        return true;
      }
      setMessage(`We sent a Leadgenflow verification email to ${email}. Open the link in that email to verify your address.`);
      return false;
    } catch (error) {
      console.error('Error checking verification status:', error);
      setMessage(`Verify ${email} before continuing.`);
      return false;
    } finally {
      setChecking(false);
    }
  };

  const verifyToken = async (token) => {
    setChecking(true);
    try {
      await api.post('/auth/verify-email', { token });
      setMessage('Your email address has been verified successfully. Redirecting to your dashboard...');
      setResendMessage('');
      window.setTimeout(() => navigate('/dashboard', { replace: true }), 500);
      return true;
    } catch (error) {
      console.error('Email verification failed:', error);
      setMessage(error?.response?.data?.error || 'This verification link is invalid or has expired. Request a new verification email below.');
      return false;
    } finally {
      setChecking(false);
    }
  };

  const sendVerificationEmail = async ({ automatic = false } = {}) => {
    setLoading(true);
    if (!automatic) setResendMessage('');
    try {
      const response = await api.post('/auth/resend-verification');
      if (response.data?.alreadyVerified) {
        await checkStatus();
        return;
      }
      if (response.data?.cooldown) {
        setResendMessage(`A verification email was sent recently. Please check your inbox and spam folder, or try again in ${response.data.retryAfterSeconds || 60} seconds.`);
      } else {
        setResendMessage('Verification email sent by Leadgenflow. Please check your inbox and spam folder.');
      }
      setMessage(`A verification email has been sent to ${email}. Click the button in that email to continue.`);
    } catch (error) {
      console.error('Error sending verification email:', error);
      const apiMessage = error?.response?.data?.error;
      setResendMessage(apiMessage || 'The verification email could not be sent. Please try again later.');
      setMessage('Your email address still needs verification before you can continue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      if (verificationToken) {
        await verifyToken(verificationToken);
        return;
      }
      const verified = await checkStatus({ navigateWhenVerified: true });
      if (cancelled || verified || autoSent.current) return;
      autoSent.current = true;
      await sendVerificationEmail({ automatic: true });
    };
    initialize();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-confirmation">
      <div className="confirmation">
        <img className="logo" src="/logo.png" alt="Leadgenflow" />
        <h1 className="confirmation-title">Email verification required</h1>
        <p className="confirmation-message">{message || 'Checking your verification status...'}</p>

        {!verificationToken && (
          <>
            <button onClick={() => checkStatus()} disabled={checking || loading} className="form-button">
              {checking ? 'Checking...' : 'I already verified my email'}
            </button>

            <button onClick={() => sendVerificationEmail()} disabled={loading || checking} className="form-button" style={{ marginTop: '12px' }}>
              {loading ? 'Sending...' : 'Resend verification email'}
            </button>
          </>
        )}

        {resendMessage && <div className="confirmation-message">{resendMessage}</div>}
        <div className="confirmation-links">
          <a href="/login" className="confirmation-link">Back to login</a>
          <a href="/contact-support" className="confirmation-link">Contact Support</a>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
